import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../lib/supabase';
import { getTenant } from '../lib/tenant';
import { Check, X, AlertTriangle, Key } from 'lucide-react';
import { API_BASE, apiJson, postRedsysForm } from '../lib/api';
import { useFiscalAdapter } from '../lib/fiscal/useFiscalAdapter';
import { buildPlans } from '../lib/plans';
import RenewalDialog from '../components/RenewalDialog';
import {
    dbPlanFor, cardForDbPlan, pendingPlanChanges,
    rememberRenewal, recallRenewal, forgetRenewal, renewalRecorded,
} from '../lib/renewal';

const CONTACT_EMAIL = 'info@zcom.es';

export default function SubscriptionDashboard() {
    const { t } = useTranslation();
    const { adapter } = useFiscalAdapter();
    const isSpain = adapter.countryCode === 'ES';
    const cur = adapter.meta.currencySymbol;
    const taxLabel = adapter.meta.taxLabel;

    const [profile, setProfile] = useState(null);
    const [licenses, setLicenses] = useState([]);
    const [dbPlans, setDbPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isAnnual, setIsAnnual] = useState(true);
    // Return from the Redsys gateway: ?success=true (URL OK) / ?error=true (URL KO).
    // 'checking' = paid, waiting for the webhook to activate the account;
    // 'active' = activated (page reloads so the locked shell lets the user in);
    // 'pending' = paid but not activated within the wait; 'failed' = KO.
    // A renewal paid from an account that is already active goes through
    // 'renewChecking' (waiting for the expiry date to move) and 'renewed'.
    const query = (() => {
        try { return new URLSearchParams(window.location.search); } catch (e) { return new URLSearchParams(); }
    })();
    const returnFlag = (() => {
        if (query.get('success') === 'true') return 'checking';
        if (query.get('error') === 'true') return 'failed';
        if (query.get('renewed') === '1') return 'renewed';
        return null;
    })();
    const [payState, setPayState] = useState(returnFlag);
    // Renewal dialog: null = closed, otherwise the plan card it opens on
    // ('' = no plan selected yet). ?renew=1 (the «Renovar ahora» reminder bar
    // and the reminder emails) opens it as soon as the account has loaded.
    const [renewFor, setRenewFor] = useState(null);
    const [renewAsked, setRenewAsked] = useState(query.get('renew') === '1');
    const [paying, setPaying] = useState(false);
    // Plans already paid for that start when the running period ends.
    const [planChanges, setPlanChanges] = useState([]);
    // Whether the backend asks Redsys to store the card (BILLING_AUTO_CHARGE):
    // decides if the checkout shows the recurring-payment consent line.
    const [autoChargeOn, setAutoChargeOn] = useState(false);
    // Facturas zBilling issued to this account for its subscription charges.
    const [subInvoices, setSubInvoices] = useState([]);
    const [savingRenew, setSavingRenew] = useState(false);

    useEffect(() => {
        fetchData();
        fetch(`${API_BASE}/api/redsys/status`)
            .then(r => (r.ok ? r.json() : null))
            .then(d => { if (d && typeof d.auto_charge === 'boolean') setAutoChargeOn(d.auto_charge); })
            .catch(() => {});
    }, []);

    // The client can switch automatic renewal off (we then only send reminders
    // and they pay by hand) or back on while their card token is still stored.
    const openInvoicePdf = async (number) => {
        try {
            const { data: { session } } = await supabase.auth.getSession();
            const res = await fetch(`${API_BASE}/api/subscription-invoices/${encodeURIComponent(number)}/pdf`, {
                headers: { Authorization: `Bearer ${session?.access_token || ''}` },
            });
            if (!res.ok) throw new Error(res.statusText);
            const url = URL.createObjectURL(await res.blob());
            window.open(url, '_blank');
            setTimeout(() => URL.revokeObjectURL(url), 60000);
        } catch (e) {
            alert(t('subdash.invoiceOpenError'));
        }
    };

    const setAutoRenew = async (on) => {
        if (!profile) return;
        setSavingRenew(true);
        try {
            const { error } = await supabase.from('profiles').update({ auto_renew: on }).eq('id', profile.id);
            if (!error) setProfile({ ...profile, auto_renew: on });
        } finally {
            setSavingRenew(false);
        }
    };

    // After a successful payment Redsys notifies our backend server-to-server;
    // that can land a few seconds after the browser comes back. Poll the owner
    // profile until it is active, then reload so Layout drops the lock screen.
    useEffect(() => {
        if (payState === 'failed') forgetRenewal();
        if (payState !== 'checking' && payState !== 'renewChecking') return undefined;
        let tries = 0;
        let cancelled = false;
        const tick = async () => {
            if (cancelled) return;
            tries += 1;
            try {
                const { ownerId } = await getTenant();
                const { data } = await supabase.from('profiles')
                    .select('subscription_status, account_status, next_billing_date').eq('id', ownerId).single();
                // A renewal: the account was active before paying, so wait for
                // the expiry date to move instead of for the status.
                const renewal = recallRenewal(ownerId);
                if (renewal) {
                    if (cancelled) return;
                    if (renewalRecorded(renewal, data)) {
                        forgetRenewal();
                        // Full reload: the reminder bar in the shell reads the date too.
                        window.location.replace('/subscription?renewed=1');
                        return;
                    }
                    if (payState !== 'renewChecking') { setPayState('renewChecking'); return; }
                } else if (data && data.subscription_status === 'active' && data.account_status !== 'suspended') {
                    setPayState('active');
                    setTimeout(() => window.location.replace('/dashboard'), 1500);
                    return;
                }
            } catch (e) { /* keep polling */ }
            if (tries >= 20) { setPayState('pending'); return; }
            setTimeout(tick, 3000);
        };
        tick();
        return () => { cancelled = true; };
    }, [payState]);

    const fetchData = async () => {
        const { user, ownerId } = await getTenant();
        if (!user) return;

        // Team members see the company's (employer's) subscription, not their own.
        const { data: pData } = await supabase.from('profiles').select('*').eq('id', ownerId).single();
        let currentProfile = pData;
        if (!currentProfile) {
            currentProfile = {
                id: ownerId,
                subscription_status: 'trial',
                trial_end_date: new Date(new Date().getTime() + 20 * 24 * 60 * 60 * 1000).toISOString(),
                next_billing_date: null,
                subscription_plan_id: null
            };
        }
        setProfile(currentProfile);

        const { data: lData } = await supabase.from('licenses').select('*').eq('user_id', ownerId);
        if (lData) setLicenses(lData);

        const { data: invData } = await supabase.from('subscription_invoices')
            .select('number, issued_at, plan_name, amount, period_start, period_end')
            .eq('user_id', ownerId).order('issued_at', { ascending: false }).limit(50);
        setSubInvoices(invData || []);

        const { data: plansData } = await supabase.from('plans').select('*');
        if (plansData) setDbPlans(plansData);

        const { data: changes } = await supabase.from('subscription_billing_log')
            .select('plan_id, ref_date, status')
            .eq('user_id', ownerId).eq('type', 'plan_change').eq('status', 'pending');
        setPlanChanges(pendingPlanChanges(changes));

        setLoading(false);
    };

    // "Plan a Medida" carries no public price — it is quoted per client, so its
    // card opens an email to sales instead of going through Redsys checkout.
    const contactForCustomPlan = () => {
        window.location.href = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(t('subdash.customMailSubject'))}`;
    };

    // Send the client to the payment gateway for one cycle of a plan.
    // `busy` is the page loader for the plan cards and the dialog's own
    // "redirecting" state when the payment starts from the renewal dialog.
    const checkout = async (planObj, annual, busy) => {
        try {
            busy(true);
            const { ownerId } = await getTenant();

            // Match the UI plan name with the actual ID from the database.
            // Accent/case-insensitive so "Plan Básico" matches a stored "Plan Basico".
            const matchedPlan = dbPlanFor(planObj, dbPlans);
            if (!matchedPlan) {
                throw new Error(t('subdash.planSyncError'));
            }

            // What this page showed, in cents (e.g. 100 EUR = 10000).
            const shownCents = Math.round((annual ? planObj.annualBilled : planObj.priceMonthly) * 100);

            // Sent with the session: the server charges the account of whoever
            // is signed in, at the plan's price for the chosen cycle. It ignores
            // user_id and amount — they only travel for a server that has not
            // been updated yet.
            const data = await apiJson('/api/redsys/create-payment', {
                method: 'POST',
                body: {
                    plan_id: matchedPlan.id,
                    user_id: ownerId,
                    amount: shownCents,
                    // What the client chose: the webhook stores it so renewal
                    // happens monthly or yearly at exactly this amount.
                    cycle: annual ? 'yearly' : 'monthly',
                },
            });

            // Never send anyone to pay an amount they have not seen.
            const chargedCents = Number(data?.amount);
            if (Number.isFinite(chargedCents) && chargedCents > 0 && chargedCents !== shownCents) {
                const amount = (chargedCents / 100).toFixed(2).replace('.', ',');
                if (!window.confirm(t('subdash.amountChanged', { amount, cur, tax: taxLabel }))) {
                    busy(false);
                    return;
                }
            }

            // The backend decides which Redsys terminal receives the form
            // (REDSYS_ENV: test → sis-t, production → sis). Never hardcode the
            // gateway here: a leftover sis-t URL sent every live subscription
            // payment to the TEST terminal and Comercia rejected them all
            // (case 06829182).
            if (!data?.url) throw new Error(t('subdash.gatewayError'));
            // On the way back the page must know whether to wait for a first
            // activation or for a renewal of an account that is already active.
            if (profile.subscription_status === 'active') rememberRenewal(profile);
            else forgetRenewal();
            postRedsysForm({
                url: data.url,
                params: {
                    Ds_SignatureVersion: data.signature_version,
                    Ds_MerchantParameters: data.params_b64,
                    Ds_Signature: data.signature,
                },
            });

        } catch (error) {
            alert(t('subdash.gatewayConnectError', { msg: error.message }));
            busy(false);
        }
    };

    const handleSubscribe = (planObj) => checkout(planObj, isAnnual, setLoading);

    // The plan cards behind this account: the running plan, and the plan in
    // force right before a new period would start — the last one already paid
    // for and waiting for its date, else the running one.
    const accountPlans = () => {
        const cards = buildPlans(t, isSpain);
        const cardOf = (id) => cardForDbPlan(dbPlans.find(p => p.id === id), cards);
        const running = profile ? cardOf(profile.subscription_plan_id) : null;
        const waiting = planChanges.length ? cardOf(planChanges[planChanges.length - 1].plan_id) : null;
        return { running, prior: waiting || running };
    };

    // Renewing opens on the plan the client will be on; they can pick another.
    const openRenewal = (card) => {
        const preset = card || accountPlans().prior;
        setRenewFor(preset && !preset.custom ? preset.name : '');
    };

    // ?renew=1: open the renewal dialog once, as soon as the account is known.
    useEffect(() => {
        if (!renewAsked || loading || !profile) return;
        setRenewAsked(false);
        if (!isSpain || payState) return;
        const { running } = accountPlans();
        if (running && running.custom) return;  // quoted plans renew through sales
        openRenewal(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [renewAsked, loading, profile, dbPlans, planChanges, isSpain]);

    if (loading || !profile) return <div className="p-10 flex justify-center text-[#1a4f8b]">{t('subdash.loadingPlans')}</div>;

    const trialEnd = new Date(profile.trial_end_date);
    const daysLeft = Math.ceil((trialEnd - new Date()) / (1000 * 60 * 60 * 24));

    // Plan contracted by this account (null during the trial). Used to mark the
    // "Tu plan actual" card and to render the subscription summary at the top.
    const currentDbPlan = profile.subscription_plan_id
        ? dbPlans.find(p => p.id === profile.subscription_plan_id)
        : null;
    const isCurrentPlan = (uiName) =>
        currentDbPlan && (currentDbPlan.name || '').localeCompare(uiName || '', 'es', { sensitivity: 'base' }) === 0;
    const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('es-ES') : null);

    // Human-readable summary of the account's subscription state.
    const summary = (() => {
        const s = profile.subscription_status;
        if (s === 'trial') {
            return {
                badge: daysLeft > 0 ? t('subdash.badgeTrial') : t('subdash.badgeTrialEnded'),
                badgeClass: daysLeft > 0 ? 'bg-blue-100 text-blue-700' : 'bg-red-100 text-red-700',
                title: currentDbPlan ? currentDbPlan.name : t('subdash.trialPeriod'),
                detail: daysLeft > 0
                    ? t('subdash.trialDaysLeft', { count: daysLeft, date: fmtDate(profile.trial_end_date) })
                    : t('subdash.trialEndedDetail'),
            };
        }
        if (s === 'active') {
            return {
                badge: t('subdash.badgeActive'),
                badgeClass: 'bg-green-100 text-green-700',
                title: currentDbPlan ? currentDbPlan.name : t('subdash.planContracted'),
                detail: profile.next_billing_date
                    ? t('subdash.nextRenewal', { date: fmtDate(profile.next_billing_date) })
                    : t('subdash.activeSubscription'),
            };
        }
        return {
            badge: s === 'cancelled' ? t('subdash.badgeCancelled') : t('subdash.badgeExpired'),
            badgeClass: 'bg-red-100 text-red-700',
            title: currentDbPlan ? currentDbPlan.name : t('subdash.noActivePlan'),
            detail: t('subdash.subscriptionInactive'),
        };
    })();

    const plans = buildPlans(t, isSpain);
    const pricedPlans = plans.filter(p => !p.custom);
    const isActive = profile.subscription_status === 'active';
    // The card of the running plan (null when the account's plan has no
    // card) and of the plan in force right before a new period starts.
    const { running: currentCard, prior: priorCard } = accountPlans();
    // Plans already paid for that start when the running period ends.
    const scheduled = planChanges
        .map(c => ({ ...c, plan: dbPlans.find(p => p.id === c.plan_id) }))
        .filter(c => c.plan);
    // Accounts on a quoted plan renew through sales, like they contracted it.
    const renewsBySales = !!(currentCard && currentCard.custom);

    const payBanner = payState && {
        checking: { cls: 'bg-green-600', text: t('subdash.paidChecking') },
        renewChecking: { cls: 'bg-green-600', text: t('subdash.renewChecking') },
        renewed:  { cls: 'bg-green-600', text: profile.next_billing_date
            ? t('subdash.renewedUntil', { date: fmtDate(profile.next_billing_date) })
            : t('subdash.renewedDone') },
        active:   { cls: 'bg-green-600', text: t('subdash.paidActive') },
        pending:  { cls: 'bg-amber-500', text: t('subdash.paidPending', { email: CONTACT_EMAIL }) },
        failed:   { cls: 'bg-red-600',   text: t('subdash.paidFailed') },
    }[payState];

    return (
        <div className="bg-[#f8fafc] min-h-screen pb-20">
            {payBanner && (
                <div className={`${payBanner.cls} text-white text-center py-3 px-4 flex items-center justify-center gap-2 text-sm font-semibold`}>
                    {payState === 'failed' ? <X className="w-4 h-4" /> : <Check className="w-4 h-4" />}
                    {payBanner.text}
                </div>
            )}
            {profile.subscription_status === 'trial' && !payBanner && (
                <div className="bg-[#1a4f8b] text-white text-center py-3 px-4 flex items-center justify-center gap-2 text-sm font-medium">
                    <AlertTriangle className="w-4 h-4 text-[#fca311]" />
                    {daysLeft > 0
                        ? t('subdash.trialBannerActive', { count: daysLeft, date: fmtDate(profile.trial_end_date) })
                        : t('subdash.trialBannerEnded')}
                </div>
            )}

            <div className="max-w-7xl mx-auto px-4 pt-12">
                {/* Current subscription summary — visible from the moment the account is created */}
                <div className="max-w-3xl mx-auto mb-12 bg-white rounded-2xl border border-gray-200 shadow-md p-6 flex flex-col sm:flex-row sm:items-center gap-4">
                    <div className="flex-grow">
                        <div className="flex items-center gap-3 mb-1">
                            <span className={`text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide ${summary.badgeClass}`}>
                                {summary.badge}
                            </span>
                            <h2 className="text-lg font-bold text-[#1d2d3d]">{summary.title}</h2>
                        </div>
                        <p className="text-sm text-[#6d7d8b]">{summary.detail}</p>
                        {profile.subscription_status === 'active' && (
                            <div className="mt-3 text-sm">
                                {profile.redsys_cof_identifier ? (
                                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                                        <span className={`inline-flex items-center gap-1 font-semibold ${profile.auto_renew ? 'text-green-700' : 'text-amber-700'}`}>
                                            {profile.auto_renew ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                                            {profile.auto_renew ? t('subdash.autoRenewOn') : t('subdash.autoRenewOff')}
                                        </span>
                                        {profile.card_last4 && (
                                            <span className="text-[#6d7d8b]">
                                                {t('subdash.cardOnFile', { last4: profile.card_last4 })}
                                            </span>
                                        )}
                                        <button
                                            type="button"
                                            disabled={savingRenew}
                                            onClick={() => setAutoRenew(!profile.auto_renew)}
                                            className="text-[#1a4f8b] underline font-semibold disabled:opacity-50"
                                        >
                                            {profile.auto_renew ? t('subdash.autoRenewDisable') : t('subdash.autoRenewEnable')}
                                        </button>
                                    </div>
                                ) : (
                                    <span className="text-[#6d7d8b]">{t('subdash.manualRenewal')}</span>
                                )}
                            </div>
                        )}
                        {scheduled.map(c => (
                            <p key={`${c.plan_id}-${c.ref_date}`} className="mt-2 text-sm font-semibold text-[#1a4f8b]">
                                {t('subdash.scheduledPlan', { plan: c.plan.name, date: fmtDate(c.ref_date) })}
                            </p>
                        ))}
                    </div>
                    {isActive && isSpain && (
                        <div className="flex-shrink-0">
                            <button
                                type="button"
                                onClick={renewsBySales ? contactForCustomPlan : () => openRenewal(null)}
                                className="w-full sm:w-auto px-6 py-3 rounded-full font-bold text-white bg-[#fca311] hover:bg-orange-500 transition-colors text-sm shadow-md"
                            >
                                {renewsBySales ? t('subdash.contactUs') : t('subdash.renewNow')}
                            </button>
                        </div>
                    )}
                    {profile.subscription_status === 'trial' && daysLeft > 0 && (
                        <div className="flex-shrink-0 text-center bg-[#f8fafc] border border-gray-200 rounded-xl px-5 py-3">
                            <div className="text-3xl font-bold text-[#1a4f8b] leading-none">{daysLeft}</div>
                            <div className="text-[11px] font-semibold text-[#6d7d8b] uppercase tracking-wide mt-1">
                                {t('subdash.daysRemaining', { count: daysLeft })}
                            </div>
                        </div>
                    )}
                </div>

                {subInvoices.length > 0 && (
                    <div className="max-w-3xl mx-auto mb-12 bg-white rounded-2xl border border-gray-200 shadow-md p-6">
                        <h3 className="text-sm font-bold text-[#1d2d3d] uppercase tracking-wide mb-3">{t('subdash.invoicesTitle')}</h3>
                        <div className="divide-y divide-gray-100">
                            {subInvoices.map(inv => (
                                <div key={inv.number} className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm">
                                    <div>
                                        <span className="font-semibold text-[#1d2d3d]">{inv.number}</span>
                                        <span className="text-[#6d7d8b]"> · {fmtDate(inv.issued_at)} · {inv.plan_name}</span>
                                        {inv.period_start && inv.period_end && (
                                            <span className="text-[#6d7d8b]"> ({fmtDate(inv.period_start)} – {fmtDate(inv.period_end)})</span>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <span className="font-bold text-[#1d2d3d]">{Number(inv.amount || 0).toFixed(2).replace('.', ',')} {cur}</span>
                                        <button type="button" onClick={() => openInvoicePdf(inv.number)}
                                                className="text-[#1a4f8b] font-semibold underline">{t('subdash.invoicePdf')}</button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Plans, prices and card payment are Spain-only (EUR via Redsys).
                    PK accounts don't see pricing — billing there is arranged offline,
                    so we show a short note instead of the plan cards. */}
                {isSpain ? (
                  <>
                {/* Toggle Switch */}
                <div className="flex flex-col items-center justify-center mb-12">
                    <div className="flex items-center gap-3">
                        <span className="text-xs font-bold text-orange-500 tracking-wide">{t('subdash.save17')}</span>
                        <span className={`text-sm font-bold ${isAnnual ? 'text-[#1d2d3d]' : 'text-gray-400'}`}>{t('subdash.annual')}</span>

                        <button
                            className={`w-14 h-7 rounded-full relative transition-colors ${isAnnual ? 'bg-[#1a4f8b]' : 'bg-gray-300'}`}
                            onClick={() => setIsAnnual(!isAnnual)}
                        >
                            <div className={`w-5 h-5 bg-white rounded-full absolute top-1 transition-transform ${isAnnual ? 'left-1' : 'left-8'}`} />
                        </button>

                        <span className={`text-sm font-bold ${!isAnnual ? 'text-[#1d2d3d]' : 'text-gray-400'}`}>{t('subdash.monthly')}</span>
                    </div>
                    {autoChargeOn && (
                        <p className="text-center text-xs text-[#6d7d8b] max-w-2xl mx-auto mt-4">
                            {t(isAnnual ? 'subdash.recurringConsentYearly' : 'subdash.recurringConsentMonthly')}
                        </p>
                    )}
                </div>

                {/* Pricing Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 items-stretch gap-6 mt-8">
                    {plans.map((plan, index) => {
                        const current = isCurrentPlan(plan.name);
                        return (
                        <div
                            key={plan.name}
                            className={`
                                relative bg-white rounded-2xl flex flex-col w-full mt-4
                                ${current ? 'border-2 border-green-500 shadow-2xl' : plan.popular ? 'border-2 border-blue-200 shadow-2xl' : plan.custom ? 'border-2 border-[#fca311]/40 shadow-lg' : 'border border-gray-200 shadow-md'}
                            `}
                        >
                            {current ? (
                                <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-green-500 text-white font-bold px-4 py-1 rounded-full text-xs">
                                    {t('subdash.yourCurrentPlan')}
                                </div>
                            ) : plan.popular && (
                                <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-blue-100 text-blue-700 font-bold px-4 py-1 rounded-full text-xs">
                                    {t('subdash.mostPopular')}
                                </div>
                            )}

                            <div className="p-8 pb-6 border-b border-gray-100 flex-grow-0">
                                <h3 className="text-xl font-bold text-[#1d2d3d] font-serif">{plan.name}</h3>
                                <p className="text-xs text-gray-500 mt-1 h-4">{plan.subtitle}</p>

                                {plan.custom ? (
                                    <>
                                        <div className="mt-6 h-12 flex items-center text-blue-600">
                                            <span className="text-3xl font-light tracking-tight">{t('subdash.customPrice')}</span>
                                        </div>
                                        <p className="text-[10px] text-gray-400 mt-2 h-4">{t('subdash.customPriceNote')}</p>
                                    </>
                                ) : (
                                    <>
                                        <div className="mt-6 flex items-start text-blue-600">
                                            <span className="text-5xl font-light tracking-tighter">
                                                {(isAnnual ? plan.priceAnnual : plan.priceMonthly).toString().replace('.', ',')}
                                            </span>
                                            <div className="flex flex-col ml-1 mt-1">
                                                <span className="font-bold text-sm">{cur}/{t('subdash.perMonth')}</span>
                                                <span className="text-[10px] font-bold text-blue-600 mt-0.5">{t('subdash.taxIncluded', { tax: taxLabel })}</span>
                                            </div>
                                        </div>
                                        <p className="text-[10px] text-gray-400 mt-2 h-4">
                                            {isAnnual
                                                ? t('subdash.billedAnnually', { amount: plan.annualBilled, cur, tax: taxLabel })
                                                : t('subdash.billedMonthly', { amount: plan.priceMonthly * 12, cur, tax: taxLabel })}
                                        </p>
                                    </>
                                )}
                            </div>

                            <div className="p-8 pt-6 flex-grow">
                                <ul className="space-y-4">
                                    {plan.features.map((feature, i) => (
                                        <li key={i} className="flex items-start gap-3 text-xs text-[#1d2d3d]">
                                            {feature.included ? (
                                                <Check className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" strokeWidth={3} />
                                            ) : (
                                                <X className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" strokeWidth={3} />
                                            )}
                                            <span className="leading-tight">
                                                {feature.badge && (
                                                    <span className={`${feature.badgeColor} text-white text-[9px] font-bold px-1.5 py-0.5 rounded mr-1.5 uppercase`}>
                                                        {feature.badge}
                                                    </span>
                                                )}
                                                {feature.text}
                                                {feature.badgeAfter && (
                                                    <span className="ml-1.5 bg-amber-100 text-amber-700 text-[9px] font-bold px-1.5 py-0.5 rounded uppercase align-middle">
                                                        {feature.badgeAfter}
                                                    </span>
                                                )}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            <div className="p-8 pt-0 mt-auto">
                                {plan.custom && !(current && profile.subscription_status === 'active') ? (
                                    <button
                                        onClick={contactForCustomPlan}
                                        className="w-full py-3.5 rounded-full font-bold text-white bg-[#1a4f8b] hover:bg-[#123963] transition-colors text-sm shadow-md"
                                    >
                                        {t('subdash.contactUs')}
                                    </button>
                                ) : plan.custom ? (
                                    <button
                                        disabled
                                        className="w-full py-3.5 rounded-full font-bold text-white bg-green-500 text-sm shadow-md cursor-default"
                                    >
                                        {t('subdash.activePlan')}
                                    </button>
                                ) : isActive ? (
                                    // An account with a plan renews it, or moves to this
                                    // one, through the dialog: it shows the dates first.
                                    <button
                                        onClick={() => openRenewal(plan)}
                                        className={`w-full py-3.5 rounded-full font-bold text-white transition-colors text-sm shadow-md ${current ? 'bg-green-500 hover:bg-green-600' : 'bg-[#fca311] hover:bg-orange-500'}`}
                                    >
                                        {current ? t('subdash.renewPlan') : t('subdash.switchToPlan')}
                                    </button>
                                ) : (
                                    <button
                                        onClick={() => handleSubscribe(plan)}
                                        disabled={loading}
                                        className="w-full py-3.5 rounded-full font-bold text-white bg-[#fca311] hover:bg-orange-500 transition-colors text-sm shadow-md disabled:opacity-60"
                                    >
                                        {loading ? t('subdash.redirectingToPayment') : current ? t('subdash.renewPlan') : t('subdash.subscribeNow')}
                                    </button>
                                )}
                                <p className="text-center text-[10px] text-gray-400 mt-2">
                                    {plan.custom ? t('subdash.contactUsNote') : t('subdash.securePayment')}
                                </p>
                            </div>
                        </div>
                        );
                    })}
                </div>
                  </>
                ) : (
                  <div className="max-w-3xl mx-auto mb-12 bg-white rounded-2xl border border-gray-200 shadow-md p-8 text-center">
                    <h3 className="text-lg font-bold text-[#1d2d3d] mb-2">{t('subdash.pkPricingTitle')}</h3>
                    <p className="text-sm text-[#6d7d8b]">{t('subdash.pkPricingNote')}</p>
                  </div>
                )}

                {renewFor !== null && (
                    <RenewalDialog
                        profile={profile}
                        plans={pricedPlans}
                        currentPlanName={currentCard ? currentCard.name : null}
                        priorPlanName={priorCard ? priorCard.name : null}
                        initialPlanName={renewFor}
                        initialCycle={profile.billing_cycle === 'monthly' ? 'monthly' : 'yearly'}
                        cur={cur}
                        taxLabel={taxLabel}
                        autoChargeOn={autoChargeOn}
                        busy={paying}
                        onPay={(plan, cycle) => checkout(plan, cycle === 'yearly', setPaying)}
                        onClose={() => setRenewFor(null)}
                    />
                )}

                {/* Licenses Display Section */}
                {licenses.length > 0 && (
                    <div className="mt-24 max-w-4xl mx-auto">
                        <h3 className="text-2xl font-bold text-[#1d2d3d] mb-6">{t('subdash.activeLicenses')}</h3>
                        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                            {licenses.map(lic => (
                                <div key={lic.id} className="p-6 border-b last:border-0 flex justify-between items-center bg-[#f8fafc]">
                                    <div className="flex items-center gap-4">
                                        <div className="bg-blue-100 p-3 rounded-full"><Key className="w-6 h-6 text-[#1a4f8b]" /></div>
                                        <div>
                                            <p className="font-bold text-[#1d2d3d] font-mono text-lg tracking-wider">{lic.license_key}</p>
                                            <p className="text-sm text-[#6d7d8b]">{t('subdash.validUntil', { date: new Date(lic.expires_at).toLocaleDateString() })}</p>
                                        </div>
                                    </div>
                                    <div className="bg-green-100 text-green-800 text-xs font-bold px-4 py-1.5 rounded-full uppercase tracking-wide">
                                        {t('subdash.active')}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
